/**
 * LiveKit collaboration fan-out targets (Q&A, chat DMs).
 * Room-wide payloads omit destinationIdentities.
 */

import { parseParticipantRole } from "./config";
import type { QaItem } from "./liveCollaborationTypes";

export type RoomParticipantLookup = {
  localParticipant?: { identity?: string; metadata?: string } | null;
  remoteParticipants?: Map<string, { identity?: string; metadata?: string }>;
};

export function trainerIdentitiesInRoom(room: RoomParticipantLookup): string[] {
  const out = new Set<string>();
  const local = room.localParticipant;
  const localId = String(local?.identity || "").trim();
  if (
    localId &&
    parseParticipantRole(local?.metadata, localId) === "trainer"
  ) {
    out.add(localId);
  }
  room.remoteParticipants?.forEach((p) => {
    const id = String(p.identity || "").trim();
    if (id && parseParticipantRole(p.metadata, id) === "trainer") {
      out.add(id);
    }
  });
  return Array.from(out);
}

/** Assigned session trainer + every trainer currently in the LiveKit room. */
export function qaTrainerDestinations(
  room: RoomParticipantLookup,
  assignedTrainerId?: string,
): string[] {
  const ids = new Set(trainerIdentitiesInRoom(room));
  const assigned = String(assignedTrainerId || "").trim();
  if (assigned) ids.add(assigned);
  return Array.from(ids);
}

/** Drop offline identities; room-wide when nobody targeted is connected. */
export function filterDestinationsInRoom(
  room: RoomParticipantLookup,
  dests: string[] | undefined,
): string[] | undefined {
  if (!dests?.length) return dests;
  const inRoom = new Set<string>();
  const local = String(room.localParticipant?.identity || "").trim();
  if (local) inRoom.add(local);
  room.remoteParticipants?.forEach((p) => {
    const id = String(p.identity || "").trim();
    if (id) inRoom.add(id);
  });
  const filtered = dests.filter((id) => inRoom.has(id));
  return filtered.length ? filtered : undefined;
}

/** @deprecated Use filterDestinationsInRoom */
export const filterQaDestinationsInRoom = filterDestinationsInRoom;

export function qaPublishDestinations(input: {
  item: Pick<QaItem, "status" | "visible_to_all" | "asked_by" | "pinned">;
  publisherIdentity?: string;
  /** Assigned trainer from session metadata. */
  trainerIdentity?: string;
  /** Connected trainers in the LiveKit room (primary + co-trainers). */
  trainerIdentities?: string[];
  /** Force fan-out to the whole room (show / hide / pin / unpin). */
  roomWide?: boolean;
}): string[] | undefined {
  if (input.roomWide) return undefined;
  const asker = String(input.item.asked_by?.identity || "").trim();
  const visible = Boolean(input.item.visible_to_all);
  if (visible || Boolean(input.item.pinned) || !asker) {
    return undefined;
  }
  const status = String(input.item.status || "open");
  const dest = new Set<string>();
  dest.add(asker);
  const self = String(input.publisherIdentity || "").trim();
  if (self) dest.add(self);
  // Open questions: room-wide so every trainer/co-trainer gets the packet instantly.
  // Students ignore other people's private questions client-side.
  if (status === "open") {
    return undefined;
  }
  return Array.from(dest);
}

export function chatDmDestinations(input: {
  publisherIdentity?: string;
  senderIdentity?: string;
  recipientIdentity?: string;
  trainerIdentities?: string[];
}): string[] | undefined {
  const dest = new Set<string>();
  const self = String(input.publisherIdentity || "").trim();
  const sender = String(input.senderIdentity || "").trim();
  const recipient = String(input.recipientIdentity || "").trim();
  if (self) dest.add(self);
  if (sender) dest.add(sender);
  if (recipient) dest.add(recipient);
  const trainers = (input.trainerIdentities || [])
    .map((id) => String(id || "").trim())
    .filter(Boolean);
  if (recipient && trainers.includes(recipient)) {
    for (const t of trainers) dest.add(t);
  }
  return dest.size ? Array.from(dest) : undefined;
}
